import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { messagesApi, type ConversationSummary } from '../api/endpoints.ts';
import { Alert, Button, LoadingBlock, Pagination } from '../components/ui.tsx';
import { WorkPage, WorkSection, WorkEmpty } from '../components/work-page.tsx';
import '../components/messages-presentation.css';
import { ConversationThread } from '../components/conversation-thread.tsx';
import { conversationPreviewText } from '../components/call-details.tsx';
import DemoLimitNote from '../components/demo-limit-note.tsx';
import { useSession } from '../state/session.tsx';
import { PlanRequiredPage, usePlanFeature } from '../components/plan-gate.tsx';

/**
 * The inbox.
 *
 * Until this existed there was no way to see what was waiting. Every message
 * this app could show lived on one customer's page, so answering somebody
 * meant already knowing WHO had written — which is the one thing an inbox is
 * for. A reply that arrived overnight sat unread behind a name nobody had a
 * reason to click, and the only signal anything had happened at all was a
 * notification that scrolled away.
 *
 * Two panes, because reading and answering are one action: the list says who
 * is waiting, and the thread beside it is where the reply gets typed. Choosing
 * a conversation puts it in the URL, so a notification can link straight to
 * one and a reload keeps it.
 *
 * On a phone there is no beside. Stacking the two panes put every thread under
 * the whole list, so tapping the first of five names opened a thread five rows
 * further down the page — off screen, with nothing but a scroll to say
 * anything had happened at all. Narrow, the thread opens INSIDE the row that
 * was tapped instead: the answer appears where the finger already is, and
 * tapping the name again folds it away. One layout or the other, never both at
 * once, so polling, read receipts and draft state have one owner.
 *
 * Deliberately conversation-shaped rather than customer-shaped. A text from a
 * number nobody recognizes is a real thing waiting on a real answer, and it
 * has no customer to hang off — on the customer pages those threads were
 * invisible, and in the one place they did appear they could only be filed,
 * never answered.
 */

type Filter = 'all' | 'unread' | 'unresolved';

const FILTERS: Filter[] = ['all', 'unread', 'unresolved'];

/**
 * The plan decides whether this page exists for a company at all. The check
 * is a wrapper rather than an early return inside the page, so the body's
 * hooks keep running in the same order on every render, and a Free account
 * never asks for an inbox the API would refuse.
 */
export default function MessagesPage() {
  const { t } = useTranslation();
  const session = useSession();
  // Held rows belong to the same access scope as the list that supplied them.
  const scope = JSON.stringify([
    session.user?.id,
    session.company?.id,
    session.currentLocation?.id,
    session.user?.role,
    [...(session.user?.permissions ?? [])].sort(),
    session.company?.effectivePlan,
    [...(session.company?.planFeatures ?? [])].sort(),
    session.company?.accessLocked,
    session.impersonatedBy,
  ]);
  if (!usePlanFeature('messaging')) return <PlanRequiredPage feature="messaging" title={t('messagesInbox.title')} />;
  return <MessagesPageContent key={scope} scope={scope} />;
}

function MessagesPageContent({ scope }: { scope: string }) {
  const { t } = useTranslation();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const pageRef = useRef<HTMLDivElement>(null);
  const [twoPane, setTwoPane] = useState(false);
  useLayoutEffect(() => {
    const page = pageRef.current;
    if (!page) return;
    const measure = () => {
      const rem = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      setTwoPane(page.getBoundingClientRect().width >= 48 * rem);
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(page);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const filterParam = params.get('filter') as Filter | null;
  const filter = filterParam && FILTERS.includes(filterParam) ? filterParam : 'all';
  const pageParam = Number(params.get('page'));
  const page = Number.isSafeInteger(pageParam) && pageParam > 0 ? pageParam : 1;
  const selectedId = params.get('conversation');

  const conversations = useQuery({
    queryKey: ['conversations', 'inbox', scope, filter, page],
    queryFn: () =>
      messagesApi.conversations({
        page,
        pageSize: 50,
        ...(filter === 'unread' ? { unread: true } : {}),
        ...(filter === 'unresolved' ? { unresolved: true } : {}),
      }),
  });

  const items = useMemo(() => conversations.data?.items ?? [], [conversations.data]);

  /**
   * The conversation being read, kept in the list while it is being read.
   *
   * Reading a thread marks it read, marking it read refreshes the list, and
   * under Unread a conversation that has been read is no longer in the list —
   * so opening one used to delete it out from under the person reading it.
   * The row vanished, the thread went with it, and so did whatever reply was
   * half typed into the box. The one thing you did was the thing that closed
   * it.
   *
   * So a chosen conversation is held here, with the position it was chosen
   * from, and put back into the list for exactly as long as it stays open. It
   * leaves on its own terms: closing it, choosing another, or changing the
   * filter. Held rather than re-fetched because there is nothing to fetch — it
   * is no longer in the answer to this filter's question, which is precisely
   * the point.
   */
  const [held, setHeld] = useState<{
    conversation: ConversationSummary;
    index: number;
    page: number;
    filter: Filter;
  } | null>(null);

  // A reload or notification can select a row without a click. Capture it
  // before the thread's mark-read refresh removes it from the Unread page.
  useLayoutEffect(() => {
    const index = items.findIndex((conversation) => conversation.id === selectedId);
    if (index < 0) return;
    setHeld((current) =>
      current?.conversation.id === selectedId && current.page === page && current.filter === filter
        ? current
        : { conversation: items[index]!, index, page, filter },
    );
  }, [items, selectedId, page, filter]);

  const rows = useMemo(() => {
    if (!selectedId || held?.conversation.id !== selectedId || held.page !== page || held.filter !== filter)
      return items;
    if (items.some((conversation) => conversation.id === selectedId)) return items;
    const kept = [...items];
    // Back where it was read from. Appending would move the row under the
    // reader's thumb, which is the same complaint in a smaller form.
    kept.splice(Math.min(held.index, kept.length), 0, { ...held.conversation, unreadCount: 0 });
    return kept;
  }, [items, held, selectedId, page, filter]);

  const selected = rows.find((conversation) => conversation.id === selectedId) ?? null;

  const choose = (conversation: ConversationSummary | null, index = 0) => {
    setHeld(conversation ? { conversation, index, page, filter } : null);
    const next = new URLSearchParams(params);
    if (conversation) next.set('conversation', conversation.id);
    else next.delete('conversation');
    setParams(next, { replace: true });
  };

  const setFilter = (next: Filter) => {
    const query = new URLSearchParams(params);
    query.set('filter', next);
    // The chosen thread rarely survives a filter change, and a selection
    // pointing at a row that is no longer in the list reads as a broken page.
    query.delete('conversation');
    query.delete('page');
    setHeld(null);
    setParams(query, { replace: true });
  };

  const setPage = (next: number) => {
    const query = new URLSearchParams(params);
    if (next > 1) query.set('page', String(next));
    else query.delete('page');
    query.delete('conversation');
    setHeld(null);
    setParams(query, { replace: true });
  };

  // Marking the last unread row read can shrink away its page. Keep that
  // page while the thread is open; closing it lets the shared pager recover.
  const totalPages = conversations.data?.totalPages;
  const visibleTotalPages = selected && totalPages !== undefined ? Math.max(page, totalPages) : totalPages;

  /**
   * The open conversation, wherever this layout puts it.
   *
   * One function called from one of two places rather than two call sites,
   * because it is one thread: the component behind it polls, marks the
   * conversation read on arrival and holds whatever reply is half typed, and
   * two mounted copies would do all of that twice — including marking read a
   * thread nobody can see.
   */
  const thread = (conversation: ConversationSummary) => (
    <>
      {conversation.customerId ? (
        <div className="mb-2">
          <Link className="underline" to={`/customers/${conversation.customerId}`}>
            {t('messagesInbox.openCustomer')}
          </Link>
        </div>
      ) : (
        <Alert tone="info">{t('messagesInbox.unattachedHint')}</Alert>
      )}
      <ConversationThread
        conversationId={conversation.id}
        customerId={conversation.customerId}
        canWrite={can('customers:write')}
      />
    </>
  );

  return (
    <div ref={pageRef} className="messages-page" data-layout={twoPane ? 'split' : 'inline'}>
      <WorkPage title={t('messagesInbox.title')} description={t('messagesInbox.subtitle')}>
        <DemoLimitNote>{t('publicDemo.messagesNote')}</DemoLimitNote>
        <div className="flex flex-wrap gap-2" role="group" aria-label={t('messagesInbox.filters')}>
          {FILTERS.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={filter === value}
              className={filter === value ? 'btn btn-primary' : 'btn btn-secondary'}
              onClick={() => setFilter(value)}
            >
              {t(`messagesInbox.filter${value.charAt(0).toUpperCase()}${value.slice(1)}`)}
            </button>
          ))}
        </div>

        <div className="messages-grid">
          <WorkSection title={t('messagesInbox.listLabel')}>
            {conversations.isError ? <Alert tone="error">{t('messagesInbox.loadError')}</Alert> : null}
            {conversations.isError ? (
              <Button busy={conversations.isFetching} onClick={() => void conversations.refetch()}>
                {t('messagesInbox.retry')}
              </Button>
            ) : null}
            {conversations.isLoading ? <LoadingBlock /> : null}
            {/* `min-w-0`, or the page scrolls sideways on a phone. A grid item's
            automatic minimum is its min-content width, and the previews below
            are `truncate` — nowrap text whose min-content width is the WHOLE
            preview, ellipsis or no ellipsis. One customer whose last message
            was a booking link was enough to make the column, and with it the
            document, twice the width of the screen. */}
            <ol aria-label={t('messagesInbox.listLabel')} className="flex min-w-0 flex-col gap-1">
              {rows.map((conversation, index) => {
                const open = conversation.id === selectedId;
                const inline = open && !twoPane;
                return (
                  <li key={conversation.id}>
                    <button
                      type="button"
                      id={rowId(conversation.id)}
                      // Narrow, the row IS the disclosure: it says whether its own
                      // thread is open under it, and tapping it again folds it
                      // back up. Wide, it selects a pane that is always there, and
                      // claiming to expand something would be a lie.
                      {...(twoPane
                        ? { 'aria-current': open }
                        : { 'aria-expanded': open, 'aria-controls': threadId(conversation.id) })}
                      onClick={() => choose(inline ? null : conversation, index)}
                      className={[
                        'messages-row w-full border p-3 text-left',
                        inline ? 'rounded-t' : 'rounded',
                        open
                          ? 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)]'
                          : 'border-[color:var(--color-line)]',
                      ].join(' ')}
                    >
                      <span className="flex flex-wrap items-baseline justify-between gap-2">
                        <span className="min-w-0 truncate font-medium">
                          {conversation.customerName ?? t('messagesInbox.unknownSender')}
                        </span>
                        {/* The count in the text, not a bare dot: it is announced,
                        and it survives the row being narrow. */}
                        {conversation.unreadCount > 0 ? (
                          <span className="flex flex-wrap items-baseline gap-1">
                            {/* An unheard voicemail says so in words: a caller
                            waiting for a call back is a different job from a
                            text waiting for a reply. */}
                            {conversation.lastVoicemail ? (
                              <span className="messages-voicemail rounded border border-[color:var(--color-line)] px-1.5 text-xs font-semibold">
                                {t('messagesInbox.newVoicemail')}
                              </span>
                            ) : null}
                            <span className="messages-unread text-xs font-semibold">
                              {t('messagesInbox.unread', { count: conversation.unreadCount })}
                            </span>
                          </span>
                        ) : null}
                      </span>
                      <span className="mt-1 block truncate text-sm text-[color:var(--color-muted)]">
                        {/* What it is about, where an assist has said so — the
                        summary reads better than the first 140 characters of
                        the message, and the label is what makes a list of
                        twenty scannable. Falls back to the preview, which is
                        what every row looked like before and what every row
                        still looks like at a company with the assists off. */}
                        {conversation.lastSummary ?? conversationPreviewText(conversation, t) ?? ''}
                      </span>
                      {conversation.lastIntent ? (
                        <span className="mt-1 inline-block rounded border border-[color:var(--color-line)] px-1.5 py-0.5 text-xs">
                          {t(`messagesInbox.intent.${conversation.lastIntent}`, {
                            defaultValue: conversation.lastIntent,
                          })}
                        </span>
                      ) : null}
                    </button>

                    {inline ? (
                      // Attached to the row above it rather than floating under
                      // it: at a glance the thread belongs to that name, not to
                      // the list.
                      <div
                        id={threadId(conversation.id)}
                        className="messages-inline rounded-b border border-t-0 border-[color:var(--color-brand)] p-3"
                      >
                        {thread(conversation)}
                        {/* A read thread is taller than the phone, so the row that
                        closes it is long gone by the time anybody wants it
                        closed. This one is where the reading ended, and it
                        puts the list back under the name that was tapped. */}
                        <div className="mt-3">
                          <Button
                            onClick={() => {
                              const row = document.getElementById(rowId(conversation.id));
                              choose(null);
                              // Focus goes back to the row, not just the viewport.
                              // Closing unmounts the button that was pressed, and
                              // focus left on a removed node falls to the document
                              // body — so the next Tab or swipe would start again
                              // at the top of the page rather than carrying on
                              // from the conversation just read.
                              row?.focus();
                              row?.scrollIntoView?.({ block: 'nearest' });
                            }}
                          >
                            {t('messagesInbox.closeThread')}
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </li>
                );
              })}

              {!conversations.isLoading && !conversations.isError && rows.length === 0 ? (
                <li>
                  <WorkEmpty
                    title={
                      filter === 'unread'
                        ? t('messagesInbox.emptyUnread')
                        : filter === 'unresolved'
                          ? t('messagesInbox.emptyUnresolved')
                          : t('messagesInbox.empty')
                    }
                  >
                    {filter === 'all' ? (
                      t('messagesInbox.emptyHint')
                    ) : (
                      <Button onClick={() => setFilter('all')}>{t('messagesInbox.showAll')}</Button>
                    )}
                  </WorkEmpty>
                </li>
              ) : null}
            </ol>
            <Pagination page={page} totalPages={visibleTotalPages} onPageChange={setPage} />
          </WorkSection>

          {/* Narrow, there is no second column to hold anything: the thread is
            in the list, and a lone "choose a conversation" under five tappable
            names is a sentence explaining a list to somebody already reading
            it. */}
          {twoPane ? (
            <WorkSection
              title={
                selected?.customerName ??
                (selected ? t('messagesInbox.unknownSender') : t('messagesInbox.conversation'))
              }
            >
              {selected ? thread(selected) : <WorkEmpty title={t('messagesInbox.pick')} />}
            </WorkSection>
          ) : null}
        </div>

        {conversations.isFetching && !conversations.isLoading ? (
          <p className="sr-only" role="status">
            {t('common.loading')}
          </p>
        ) : null}
      </WorkPage>
    </div>
  );
}

const rowId = (conversationId: string) => `conversation-row-${conversationId}`;
const threadId = (conversationId: string) => `conversation-thread-${conversationId}`;
